/**
 * ポリゴン関連のジオメトリ計算。
 *
 * - polygonCentroid: 単純算術重心
 * - nearestPointOnPolygonBoundary: 境界線上の最近接点 (turf使用)
 * - resolveRouteEndpoint: 物件→避難場所の経路終点を決定 (入口優先 → 境界最近接 → 重心)
 */
import {
  along as turfAlong,
  bearing as turfBearing,
  booleanPointInPolygon,
  centerOfMass as turfCenterOfMass,
  destination as turfDestination,
  length as turfLength,
  lineIntersect,
  lineString,
  midpoint as turfMidpoint,
  nearestPointOnLine,
  point as turfPoint,
  pointOnFeature as turfPointOnFeature,
  polygon as turfPolygon,
  simplify as turfSimplify,
} from '@turf/turf';
import type { LngLat } from '@/types/property';
import { haversineMeters } from './distance';
import {
  fetchEntrancesInPolygon,
  type EntranceNode,
} from '@/services/overpass';

/** ポリゴンの算術重心 (rings[0] のみ使用) */
export function polygonCentroid(
  ring: Array<[number, number]>,
): LngLat | null {
  if (ring.length === 0) return null;
  const same =
    ring.length > 1 &&
    ring[0]![0] === ring[ring.length - 1]![0] &&
    ring[0]![1] === ring[ring.length - 1]![1];
  const pts = same ? ring.slice(0, -1) : ring;
  if (pts.length === 0) return null;
  const sum = pts.reduce(
    (acc, [lng, lat]) => ({ lng: acc.lng + lng, lat: acc.lat + lat }),
    { lng: 0, lat: 0 },
  );
  return { lng: sum.lng / pts.length, lat: sum.lat / pts.length };
}

/**
 * 「異常に長い弦」(> splitOverMeters) にだけ、脚が maxLegMeters 以下になるよう
 * 線形補間で中間点を挿す (端点・既存頂点は不変)。
 *
 * 中心線スナップ (route-snap.ts) の前処理用。スナップは**頂点しか動かせない**ため、
 * Mapbox 徒歩が返す長い弦 (実測: 本川小 A7 — 川沿い遊歩道 154m・中間頂点ゼロ) は
 * 中間に頂点が無いと車道へ寄せられず、車道の 20m 横を並走する矢印になる。
 * 挿した点はスナップ後に共線となり simplify が間引くので、最終形状は太らない。
 *
 * splitOverMeters 未満の通常の脚には触れない — 全脚を一律に密度化すると、広場
 * 横断など「もともと紙一重で全スナップされていた区間」の DP 均衡が崩れる
 * (実測: 本川小 C 系 — タワー前広場の 69m 脚を密度化したら、道路外 0m だった
 * 始点側に 39m の広場ランが生えた)。
 */
export function densifyRouteCoords(
  coords: Array<[number, number]>,
  maxLegMeters = 25,
  splitOverMeters = 80,
): Array<[number, number]> {
  if (coords.length < 2) return coords;
  const out: Array<[number, number]> = [coords[0]!];
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1]!;
    const b = coords[i]!;
    const kx = Math.cos((a[1] * Math.PI) / 180) * 111_320;
    const legM = Math.hypot((b[0] - a[0]) * kx, (b[1] - a[1]) * 110_540);
    const n = legM > splitOverMeters ? Math.ceil(legM / maxLegMeters) : 1;
    for (let k = 1; k <= n; k++) {
      out.push([a[0] + ((b[0] - a[0]) * k) / n, a[1] + ((b[1] - a[1]) * k) / n]);
    }
  }
  return out;
}

/**
 * 始点リードが物件塗り (startRing) の内部を横切る場合、**塗りを出る最後の交点**を
 * 新しい始点にする。
 *
 * 始点アンカーは塗りの境界上にあり、スナップ/直角化が作る直線リードは凹形
 * フットプリントの張り出しを掠めることがある (実測: 横浜ランドマークタワー —
 * 第1脚が塗りを 20m 横断・深さ 4.4m)。頂点単位の trimRouteStartInsidePolygon は
 * 「両端が外・中間が内」のセグメント横断を検出できないため、サンプルで検出して
 * 境界交点から始める。深さ 1.5m 未満の掠りは誤差として触らない。
 */
export function clipStartAtRingExit(
  coords: Array<[number, number]>,
  ring: Array<[number, number]>,
  maxLeadMeters = 120,
): Array<[number, number]> {
  if (coords.length < 2 || ring.length < 3) return coords;
  const kx = Math.cos((coords[0]![1] * Math.PI) / 180) * 111_320;
  const ky = 110_540;
  const toM = (q: [number, number]): [number, number] => [q[0] * kx, q[1] * ky];
  const ringM = ring.map(toM);
  const depthM = (pt: [number, number]): number => {
    if (!pointInsideRing({ lng: pt[0], lat: pt[1] }, ring)) return 0;
    const [px, py] = toM(pt);
    let best = Infinity;
    for (let i = 0; i < ringM.length; i++) {
      const [ax, ay] = ringM[i]!;
      const [bx, by] = ringM[(i + 1) % ringM.length]!;
      const dx = bx - ax;
      const dy = by - ay;
      const l2 = dx * dx + dy * dy;
      if (l2 <= 0) continue;
      let t = ((px - ax) * dx + (py - ay) * dy) / l2;
      t = Math.max(0, Math.min(1, t));
      best = Math.min(best, Math.hypot(px - (ax + t * dx), py - (ay + t * dy)));
    }
    return best;
  };
  // 先頭 maxLeadMeters を 2m 刻みでサンプルし、深さ 1.5m 超で内部に入る
  // 最後の位置を探す (その直後の境界脱出点が新しい始点)。
  let walked = 0;
  let lastInside: { i: number; frac: number } | null = null;
  outer: for (let i = 1; i < coords.length; i++) {
    const a = toM(coords[i - 1]!);
    const b = toM(coords[i]!);
    const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const n = Math.max(1, Math.ceil(L / 2));
    for (let k = 0; k <= n; k++) {
      if (walked + (L * k) / n > maxLeadMeters) break outer;
      const pt: [number, number] = [
        coords[i - 1]![0] + ((coords[i]![0] - coords[i - 1]![0]) * k) / n,
        coords[i - 1]![1] + ((coords[i]![1] - coords[i - 1]![1]) * k) / n,
      ];
      if (depthM(pt) > 1.5) lastInside = { i, frac: k / n };
    }
    walked += L;
  }
  if (!lastInside) return coords;
  // 脱出点: lastInside から少しずつ進めて「内部でなくなる」最初のサンプル。
  const { i, frac } = lastInside;
  const a = coords[i - 1]!;
  const b = coords[i]!;
  const aM = toM(a);
  const bM = toM(b);
  const L = Math.hypot(bM[0] - aM[0], bM[1] - aM[1]);
  const steps = Math.max(1, Math.ceil(L));
  const at = (f: number): [number, number] => [
    a[0] + (b[0] - a[0]) * f,
    a[1] + (b[1] - a[1]) * f,
  ];
  const inside = (f: number): boolean => {
    const pt = at(f);
    return pointInsideRing({ lng: pt[0], lat: pt[1] }, ring);
  };
  let lo = frac; // 内側
  let hi = -1; // 外側
  for (let k = Math.ceil(frac * steps); k <= steps; k++) {
    if (!inside(k / steps)) {
      hi = k / steps;
      break;
    }
    lo = k / steps;
  }
  if (hi < 0) return coords;
  // 境界交点を二分法で precisely に (サンプル1m刻みだと始点が最大1mずれる)
  for (let it = 0; it < 20; it++) {
    const mid = (lo + hi) / 2;
    if (inside(mid)) lo = mid;
    else hi = mid;
  }
  return [at(hi), ...coords.slice(i)];
}


/**
 * 経路の細かいガタガタを間引いて「経路に沿った直線」に近づける (Douglas-Peucker)。
 * toleranceMeters を大きくするほど直線的になる。端点は必ず保持。
 */
export function simplifyRouteCoords(
  coords: Array<[number, number]>,
  toleranceMeters = 12,
): Array<[number, number]> {
  if (coords.length <= 2) return coords;
  const tolDeg = toleranceMeters / 111_000; // ~度換算 (緯度1度≒111km)
  try {
    const simplified = turfSimplify(lineString(coords), {
      tolerance: tolDeg,
      highQuality: true,
    });
    const out = simplified.geometry.coordinates as Array<[number, number]>;
    return out.length >= 2 ? out : coords;
  } catch {
    return coords;
  }
}

/**
 * 経路の終点 (避難先の境界上) から、避難先の内部へ延長した点を末尾に追加する。
 *
 * 進入の文法: **進入点の辺に垂直に入る**。異形ポリゴンで中心 (面積重心) へ直線を
 * 引くと境界へ斜めに刺さり不自然なため (実測指摘)。垂直に予算いっぱい入れない
 * 狭い形状では、入れる分だけ垂直に入り、**範囲内で折れて中心へ向かう** —
 * はみ出すくらいなら折る、が要件。
 *
 * 終点が境界上に無い場合 (形状不明・内部フォールバック等) は従来どおり中心へ
 * 直線延長する。中心は **面積重心** (頂点の単純平均は頂点が密な側に引っ張られ、
 * 平和記念公園で 63.7m ずれた実測あり)。凹形状で面積重心が外に出る場合は内部の
 * 代表点へフォールバック。
 */
/** 直進延長で境界からこの距離 (m) 以上のクリアランスを保てること (境界沿い走行を弾く)。 */
const STRAIGHT_ENTRY_CLEAR_M = 5;
/** 進入方向と中心方向のなす角がこれ以下のときだけ中心へ直進延長する (度)。 */
const STRAIGHT_ENTRY_MAX_TURN_DEG = 35;

/**
 * 進入点から避難先の中心へ向けて直進延長した先端を返す。適用できない場合は null
 * (呼び元が直角進入へ)。
 *
 * 皇居外苑のような広い避難先では、道路が境界に沿って進入する (進入方向が境界と平行)
 * ため進入方向そのままの延長は境界沿いを走って弾かれるが、中心方向は進入方向から
 * わずかに振っただけで内部へ素直に伸びる。**進入方向と中心方向のなす角が緩い**
 * (STRAIGHT_ENTRY_MAX_TURN_DEG 以下) ときに限り、中心へ 1 本の直線で延長し、直角進入が
 * 挟む不要な折れ (境界の少し内側のジグザグ) を無くす。進入方向が中心を向いていない
 * (境界沿い進入で中心が横/後ろ) 形状では角度条件で null になり、従来の直角進入に委ねる。
 *
 * 採用は「予算いっぱいの直進脚が全区間ポリゴン内部」かつ「(進入直後を除き) 境界から
 * 一定クリアランス」を満たすときのみ。
 */
function straightEntryPoint(
  ring: Array<[number, number]>,
  coords: Array<[number, number]>,
  center: LngLat,
  budgetM: number,
): [number, number] | null {
  const n = coords.length;
  if (n < 2 || ring.length < 3 || budgetM < 1) return null;
  const entry = coords[n - 1]!;
  const kx = Math.cos((entry[1] * Math.PI) / 180) * 111_320;
  const ky = 110_540;
  // 安定した進入方向: 直前 1 点だと密度化/スナップの近接点 (~数 m) で方向がぶれるため、
  // 終点から手前へ ~30m 分さかのぼった正味変位を進入方向とする。
  const STABLE_APPROACH_M = 30;
  let ax = 0;
  let ay = 0;
  let acc = 0;
  for (let i = n - 1; i > 0 && acc < STABLE_APPROACH_M; i--) {
    const sdx = (coords[i]![0] - coords[i - 1]![0]) * kx;
    const sdy = (coords[i]![1] - coords[i - 1]![1]) * ky;
    ax += sdx;
    ay += sdy;
    acc += Math.hypot(sdx, sdy);
  }
  const al = Math.hypot(ax, ay);
  if (al < 1) return null;
  ax /= al;
  ay /= al;
  // 中心方向 (entry→center)。これへ直進する。
  let cx = (center.lng - entry[0]) * kx;
  let cy = (center.lat - entry[1]) * ky;
  const cl = Math.hypot(cx, cy);
  if (cl < 1) return null;
  cx /= cl;
  cy /= cl;
  // 進入方向と中心方向のなす角が大きい = 進入がそのまま内部へ向いていない。直角進入へ。
  if (ax * cx + ay * cy < Math.cos((STRAIGHT_ENTRY_MAX_TURN_DEG * Math.PI) / 180)) {
    return null;
  }
  // 境界辺 (entry 原点の m 平面)。
  const segs: Array<[[number, number], [number, number]]> = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    if (a[0] === b[0] && a[1] === b[1]) continue;
    segs.push([
      [(a[0] - entry[0]) * kx, (a[1] - entry[1]) * ky],
      [(b[0] - entry[0]) * kx, (b[1] - entry[1]) * ky],
    ]);
  }
  const clearanceAt = (p: [number, number]): number => {
    let best = Infinity;
    for (const [a, b] of segs) {
      const sx = b[0] - a[0];
      const sy = b[1] - a[1];
      const l2 = sx * sx + sy * sy;
      if (l2 <= 0) continue;
      let t = ((p[0] - a[0]) * sx + (p[1] - a[1]) * sy) / l2;
      t = Math.max(0, Math.min(1, t));
      best = Math.min(
        best,
        Math.hypot(p[0] - (a[0] + t * sx), p[1] - (a[1] + t * sy)),
      );
    }
    return best;
  };
  // 4m 刻みで全区間サンプル。全点が内部、かつ進入直後 (8m) 以降は境界クリアランスを
  // 確保 — 境界沿いを並走する方向を弾くため。
  for (let t = 4; t <= budgetM + 0.01; t += 4) {
    const pm: [number, number] = [cx * t, cy * t];
    const pg: [number, number] = [entry[0] + pm[0] / kx, entry[1] + pm[1] / ky];
    if (!pointInsideRing({ lng: pg[0], lat: pg[1] }, ring)) return null;
    if (t >= 8 && clearanceAt(pm) < STRAIGHT_ENTRY_CLEAR_M) return null;
  }
  return [entry[0] + (cx * budgetM) / kx, entry[1] + (cy * budgetM) / ky];
}

/** ドッグレッグと畳む末尾脚の最大長 (m)。これ以下の短い脚だけ対象。 */
const STUB_MAX_LEN_M = 30;
/** ドッグレッグと見なす接続角 (度)。これ以上鋭ければ末尾スタブとして畳む。 */
const STUB_MIN_TURN_DEG = 55;

/**
 * 末尾の「短くて鋭い折れ (境界の少し内側で横へ逸れるドッグレッグ)」を畳んで手前の頂点へ
 * 戻す。進入点候補は境界トリムを掛けないため、入口へ回り込む短いスタブ (実測: 皇居外苑で
 * 境界の 3m 内側を 22m 横走) が残り、直進延長と接続して境界の少し入った所に不要な折れを
 * 作る。短くて (≤STUB_MAX_LEN_M) 鋭い (≥STUB_MIN_TURN_DEG) 末尾脚だけを対象にするので、
 * 通常のなだらかな接近路や長い最終脚は畳まない。直進延長の起点整形専用。
 */
function dropTrailingStub(
  coords: Array<[number, number]>,
): Array<[number, number]> {
  const out = coords.slice();
  while (out.length >= 3) {
    const last = out[out.length - 1]!;
    const prev = out[out.length - 2]!;
    const prev2 = out[out.length - 3]!;
    const kx = Math.cos((last[1] * Math.PI) / 180) * 111_320;
    const ky = 110_540;
    const segLen = Math.hypot(
      (last[0] - prev[0]) * kx,
      (last[1] - prev[1]) * ky,
    );
    if (segLen > STUB_MAX_LEN_M) break;
    const a1 = Math.atan2((prev[0] - prev2[0]) * kx, (prev[1] - prev2[1]) * ky);
    const a2 = Math.atan2((last[0] - prev[0]) * kx, (last[1] - prev[1]) * ky);
    let turn = ((a2 - a1) * 180) / Math.PI;
    while (turn > 180) turn -= 360;
    while (turn < -180) turn += 360;
    if (Math.abs(turn) < STUB_MIN_TURN_DEG) break;
    out.pop();
  }
  return out;
}

/** 角の入口とみなす頂点距離 (m)。進入点がこの距離以内に頂点があれば角進入。 */
const CORNER_ENTRY_VERTEX_MAX_M = 12;
/** 角の二等分線進入で最低限確保する脚長 (m)。これ未満なら不成立。 */
const CORNER_ENTRY_MIN_LEG_M = 6;

/**
 * 角 (凸頂点) の入口では、内角二等分線方向へ 1 本の対角脚で進入する。
 *
 * 辺の法線 (直角進入) は角の近くでは隣の辺に沿って走りがちで、境界を舐める短い
 * 直進脚 + 中心への折れ = ドッグレッグ (実測: 音羽公園 — 縦長の細い公園の北西角へ
 * 北から入り、西辺沿いに真南 15m 走ってから折れる雑な矢印になっていた) を作る。
 * 二等分線は左右どちらの辺からも離れて内部へ向かう唯一の素直な方向なので、角進入は
 * これを最優先する。進入点が凸頂点の近傍 (≤CORNER_ENTRY_VERTEX_MAX_M) のときだけ
 * 適用し、辺の途中の進入 (頂点が遠い) は従来どおり直進/直角進入に委ねる。
 * 単脚 (折れなし)。深さは反対側境界の手前までに制限する。
 */
function cornerBisectorEntry(
  ring: Array<[number, number]>,
  coords: Array<[number, number]>,
  center: LngLat,
  budgetM: number,
): [number, number] | null {
  const n = coords.length;
  if (n < 1 || ring.length < 3 || budgetM < 1) return null;
  const entry = coords[n - 1]!;
  const kx = Math.cos((entry[1] * Math.PI) / 180) * 111_320;
  const ky = 110_540;
  const toM = (p: [number, number]): [number, number] => [
    (p[0] - entry[0]) * kx,
    (p[1] - entry[1]) * ky,
  ];
  const fromM = (p: [number, number]): [number, number] => [
    entry[0] + p[0] / kx,
    entry[1] + p[1] / ky,
  ];
  const N = ring.length;
  const isClosed =
    ring[0]![0] === ring[N - 1]![0] && ring[0]![1] === ring[N - 1]![1];
  const M = isClosed ? N - 1 : N;
  if (M < 3) return null;
  // 進入点に最も近い頂点。角進入はこの頂点のすぐ近くでのみ成立させる。
  let vi = -1;
  let vd = Infinity;
  for (let i = 0; i < M; i++) {
    const m = toM(ring[i]!);
    const dd = Math.hypot(m[0], m[1]);
    if (dd < vd) {
      vd = dd;
      vi = i;
    }
  }
  if (vi < 0 || vd > CORNER_ENTRY_VERTEX_MAX_M) return null;
  const v = toM(ring[vi]!);
  const pv = toM(ring[(vi - 1 + M) % M]!);
  const nv = toM(ring[(vi + 1) % M]!);
  const u1: [number, number] = [pv[0] - v[0], pv[1] - v[1]];
  const u2: [number, number] = [nv[0] - v[0], nv[1] - v[1]];
  const l1 = Math.hypot(u1[0], u1[1]);
  const l2 = Math.hypot(u2[0], u2[1]);
  if (l1 < 1e-6 || l2 < 1e-6) return null;
  let bx = u1[0] / l1 + u2[0] / l2;
  let by = u1[1] / l1 + u2[1] / l2;
  const bl = Math.hypot(bx, by);
  if (bl < 1e-6) return null; // 直線 (180°) — 角ではない
  bx /= bl;
  by /= bl;
  // 二等分線が内部を向くこと (凸頂点なら v から少し進めば内部)。逆符号も試す。
  const insideProbe = (dir: [number, number]): boolean => {
    const p = fromM([v[0] + dir[0] * 2, v[1] + dir[1] * 2]);
    return pointInsideRing({ lng: p[0], lat: p[1] }, ring);
  };
  if (!insideProbe([bx, by])) {
    bx = -bx;
    by = -by;
    if (!insideProbe([bx, by])) return null; // 凹頂点等 — 角進入は諦める
  }
  // 二等分線が概ね中心側を向くこと (辺沿いの浅い進入を弾く)。
  const cM = toM([center.lng, center.lat]);
  const cl = Math.hypot(cM[0], cM[1]);
  if (cl > 1 && (bx * cM[0] + by * cM[1]) / cl < 0) return null;
  // 進入点→内部の区間 [tIn, tOut]。進入点が境界外なら最初の交差が「入る」点。
  const segs: Array<[[number, number], [number, number]]> = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    if (a[0] === b[0] && a[1] === b[1]) continue;
    segs.push([toM(a), toM(b)]);
  }
  const startsInside = pointInsideRing({ lng: entry[0], lat: entry[1] }, ring);
  const crossings = rayCrossingsM(segs, [0, 0], [bx, by]);
  let tIn = 0;
  let tOut = Infinity;
  if (startsInside) {
    tOut = crossings[0] ?? Infinity;
  } else {
    if (crossings.length === 0) return null;
    tIn = crossings[0]!;
    tOut = crossings[1] ?? Infinity;
    if (tIn > CORNER_ENTRY_VERTEX_MAX_M * 2) return null;
  }
  const margin =
    tOut === Infinity ? 0 : Math.min(ENTRY_EDGE_MARGIN_M, (tOut - tIn) * 0.25);
  const depth = Math.min(budgetM, tOut - margin);
  if (depth - tIn < CORNER_ENTRY_MIN_LEG_M) return null;
  // 脚の中点が内部にあることを最終確認 (掠め方向を弾く)。
  const mid = fromM([bx * ((tIn + depth) / 2), by * ((tIn + depth) / 2)]);
  if (!pointInsideRing({ lng: mid[0], lat: mid[1] }, ring)) return null;
  return fromM([bx * depth, by * depth]);
}

export function extendRouteIntoPolygon(
  coords: Array<[number, number]>,
  ring: Array<[number, number]>,
  meters = 40,
): Array<[number, number]> {
  if (coords.length < 1) return coords;
  const last = coords[coords.length - 1]!;
  const center = polygonInteriorCenter(ring);
  if (!center) return coords;
  const d = haversineMeters({ lng: last[0], lat: last[1] }, center);
  if (d < 1) return coords;
  // 小さい避難先では中心の 60% までに抑える (通り越して反対側へ出るのを防ぐ)。
  const budget = Math.min(meters, d * 0.6);
  const prev = coords.length >= 2 ? coords[coords.length - 2]! : null;
  // 角 (凸頂点) の入口は内角二等分線で 1 本の対角進入 (直角進入のドッグレッグを回避)。
  const corner = cornerBisectorEntry(ring, coords, center, budget);
  if (corner) {
    // 直進と同じく末尾の短いドッグレッグを畳んでから対角脚を張り直す。
    const base = dropTrailingStub(coords);
    if (base.length < coords.length) {
      const rebisector = cornerBisectorEntry(ring, base, center, budget);
      if (rebisector) return [...base, rebisector];
    }
    return [...coords, corner];
  }
  // 進入方向がそのまま内部へ十分伸びるなら、直角進入の折れを付けず直進で延長する
  // (広い避難先で境界の少し内側に出る不要なジグザグを避ける)。
  const straight = straightEntryPoint(ring, coords, center, budget);
  if (straight) {
    // 直進採用時は、末尾の「境界の少し内側で横に逸れる短い折れ (ドッグレッグ)」を畳んで
    // から中心へ直進脚を張り直す。進入点候補 (境界トリムなし) が入口へ回り込む 22m の
    // スタブを残し、境界の少し入った所に不要な折れが出るのを防ぐ (実測: 皇居外苑)。
    const base = dropTrailingStub(coords);
    if (base.length < coords.length) {
      const restraight = straightEntryPoint(ring, base, center, budget);
      if (restraight) return [...base, restraight];
    }
    return [...coords, straight];
  }
  const perp = perpendicularEntryPoints(ring, last, center, budget, prev);
  if (perp) return [...coords, ...perp];
  // フォールバック: 中心へ直線延長 (終点が境界に付いていない場合など)。
  const frac = budget / d;
  const inside: [number, number] = [
    last[0] + (center.lng - last[0]) * frac,
    last[1] + (center.lat - last[1]) * frac,
  ];
  return [...coords, inside];
}

/** 進入点がこの距離 (m) 以内で境界に付いているときだけ直角進入を適用する。 */
const ENTRY_BOUNDARY_MAX_DIST_M = 12;
/** 垂直脚がこれ (m) 未満しか取れない狭さなら直角進入を諦めて中心延長へ。 */
const ENTRY_PERP_MIN_LEG_M = 5;
/** 進入脚と反対側境界の間に残す余白 (m)。狭い形状では奥行きの 25% に縮む。 */
const ENTRY_EDGE_MARGIN_M = 8;
/** 単脚 (折れなし) を許す先端クリアランス (m)。予算が小さい形状では予算の半分。 */
const ENTRY_TIP_CLEAR_M = 10;
/** 折れの肘に求める境界クリアランス (m)。取れる最大値の 8 割まで緩む。 */
const ENTRY_ELBOW_CLEAR_M = 6;
/** 進入方向に対する折り返し上限 (度)。これを超える折りになる進入方向は、他に
 * 成立する方向がある限り避ける (実測: 札幌 旭小 — 門の通過アプローチで 157°)。 */
const ENTRY_MAX_FOLD_DEG = 150;

/**
 * 直角進入の追加頂点列を返す。適用できない場合は null (呼び元が中心延長へ)。
 *
 * - 垂直脚が予算いっぱい取れて先端のクリアランスも十分 → 垂直の 1 点のみ。
 * - それ以外 → 垂直に「クリアランスが確保できる深さ」まで入り、中心へ折れた脚を
 *   足した 2 点。クリアランスは境界**全体**への最短距離で測る (レイ交差だけでは
 *   隣の辺と並走する脚を検出できない)。脚は余白を残して範囲内に収める。
 */
function perpendicularEntryPoints(
  ring: Array<[number, number]>,
  entry: [number, number],
  center: LngLat,
  budgetM: number,
  prev?: [number, number] | null,
): Array<[number, number]> | null {
  if (ring.length < 3) return null;
  // entry 原点のローカル平面 (m)。
  const kx = Math.cos((entry[1] * Math.PI) / 180) * 111_320;
  const ky = 110_540;
  const toM = (p: [number, number]): [number, number] => [
    (p[0] - entry[0]) * kx,
    (p[1] - entry[1]) * ky,
  ];
  const fromM = (p: [number, number]): [number, number] => [
    entry[0] + p[0] / kx,
    entry[1] + p[1] / ky,
  ];
  const segs: Array<[[number, number], [number, number]]> = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    if (a[0] === b[0] && a[1] === b[1]) continue;
    segs.push([toM(a), toM(b)]);
  }
  // 進入点近傍の辺を近い順に集める。進入点が頂点のほぼ真上に載ると、最近接辺の
  // 法線レイがポリゴンを掠めて交差しない (実測: 新川ツインビル地区の頂点入口 —
  // 中心延長へ落ち、境界の外側を並走する矢印になった)。近い順に数辺を試し、
  // 最初に成立した辺での直角進入を採用する。
  const cands: Array<{ dist: number; edge: [number, number] }> = [];
  for (const [a, b] of segs) {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const l2 = dx * dx + dy * dy;
    if (l2 <= 0) continue;
    let t = (-a[0] * dx - a[1] * dy) / l2;
    t = Math.max(0, Math.min(1, t));
    const dist = Math.hypot(a[0] + t * dx, a[1] + t * dy);
    if (dist <= ENTRY_BOUNDARY_MAX_DIST_M) cands.push({ dist, edge: [dx, dy] });
  }
  cands.sort((x, y) => x.dist - y.dist);
  const startsInside = pointInsideRing({ lng: entry[0], lat: entry[1] }, ring);
  const cM = toM([center.lng, center.lat]);

  // 方向 dir で進入した場合の [進入距離 tIn, 反対側境界 tOut]。成立しなければ null。
  // レイ交差パターンで判定する: 進入点が外側なら最初の交差が「進入する辺」、
  // 2 つ目が反対側境界。進入直後 (tIn+1m) が内部に落ちない方向 (掠め・外向き) は
  // 不成立。進入点は境界の外側に数 m ずれていることがあるため (地区型: Directions
  // の終点が歩行グラフへ吸着し境界から 5-8m 外に載る。実測: 新川ツインビル地区)、
  // 固定距離のプローブ点ではなく交差列で判定する。
  const spanFor = (
    dir: [number, number],
  ): { tIn: number; tOut: number } | null => {
    const crossings = rayCrossingsM(segs, [0, 0], dir);
    let tIn = 0;
    let tOut = Infinity;
    if (startsInside) {
      tOut = crossings[0] ?? Infinity;
    } else {
      if (crossings.length === 0) return null;
      tIn = crossings[0]!;
      tOut = crossings[1] ?? Infinity;
      // 進入交差は進入点のすぐそばにあること。遠い交差は「この場所から入る」の
      // ではなく、レイが離れた別の辺からポリゴンに入っている (実測: 新川ツイン
      // ビル地区の頂点入口 — 辺法線が 120m 先の辺経由で成立扱いになり、矢印の
      // 先端が境界の手前 (外) に落ちた)。
      if (tIn > ENTRY_BOUNDARY_MAX_DIST_M * 2) return null;
    }
    const eps = Math.min(1, (tOut - tIn) / 2);
    if (!(eps > 0)) return null;
    const probe = fromM([dir[0] * (tIn + eps), dir[1] * (tIn + eps)]);
    if (!pointInsideRing({ lng: probe[0], lat: probe[1] }, ring)) return null;
    return { tIn, tOut };
  };

  // 境界全体に対するクリアランス (最短距離, m)。レイ進行方向の交差距離 (tOut) だけ
  // では「隣の辺と数 m の距離で斜めに並走する脚」を検出できない (実測: 本川小 A1 —
  // 52m の単脚が北辺から 5.4m を並走し、矢じりが境界向き = はみ出して見えた)。
  const clearanceAtM = (p: [number, number]): number => {
    let best = Infinity;
    for (const [a, b] of segs) {
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const l2 = dx * dx + dy * dy;
      if (l2 <= 0) continue;
      let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2;
      t = Math.max(0, Math.min(1, t));
      best = Math.min(best, Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy)));
    }
    return best;
  };

  const buildFor = (
    n: [number, number],
    span: { tIn: number; tOut: number },
  ): Array<[number, number]> | null => {
    const { tIn, tOut } = span;
    const margin =
      tOut === Infinity ? 0 : Math.min(ENTRY_EDGE_MARGIN_M, (tOut - tIn) * 0.25);
    const perpMax = Math.min(budgetM, tOut - margin);
    // 進入脚のうちポリゴン内部に入る分が確保できないなら、この方向は不成立
    // (先端が境界の外・境界すれすれで終わる矢印を作らない)。
    if (perpMax - tIn < ENTRY_PERP_MIN_LEG_M) return null;
    const clearAt = (t: number) => clearanceAtM([n[0] * t, n[1] * t]);
    // 単脚 (折れなし): 予算いっぱい垂直に入れて、かつ先端が境界から十分離れる場合のみ。
    if (
      perpMax >= budgetM - 0.5 &&
      clearAt(budgetM) >= Math.min(ENTRY_TIP_CLEAR_M, budgetM * 0.5)
    ) {
      return [fromM([n[0] * budgetM, n[1] * budgetM])];
    }
    // 折れモード: 肘 (折れ点) は「内部クリアランスが確保できた最初の点」。
    // 反対側余白ぎりぎりまで潜ってから折るのではない — 境界近くを斜めに並走する
    // 脚を長く引きずると折れが遅く見える (実測: 平和記念公園で肘が 20〜61m 先)。
    // 上限は予算の 60% (折れ脚に予算を残す。実測: 本川小 A1 で折れ端 2.4m の切れ端)。
    const capT = Math.min(perpMax, budgetM * 0.6);
    const t0 = tIn + ENTRY_PERP_MIN_LEG_M;
    if (t0 > capT) return null;
    let maxClear = 0;
    for (let t = t0; t <= capT; t += 2) maxClear = Math.max(maxClear, clearAt(t));
    // どこまで進んでもクリアランスが 2m 未満 = 境界の真上を走る方向 (角の入口で
    // 辺の法線が隣の辺に沿ってしまうケース)。この方向は不成立にして、次の辺や
    // 頂点二等分線に委ねる。
    if (maxClear < 2) return null;
    const clearTarget = Math.min(ENTRY_ELBOW_CLEAR_M, maxClear * 0.8);
    let perpDepth = capT;
    for (let t = t0; t <= capT; t += 2) {
      if (clearAt(t) >= clearTarget) {
        perpDepth = t;
        break;
      }
    }
    const p1: [number, number] = [n[0] * perpDepth, n[1] * perpDepth];
    const dC = Math.hypot(cM[0] - p1[0], cM[1] - p1[1]);
    if (dC < 1) return [fromM(p1)];
    let dir: [number, number] = [(cM[0] - p1[0]) / dC, (cM[1] - p1[1]) / dC];
    // 折れは最大 90° (矢印の文法は直角まで)。中心が進入方向から 90° 超の方向なら
    // 折り返しに見えるため、進入方向に垂直な成分方向 (中心側) へ直角に折る。
    const dot = n[0] * dir[0] + n[1] * dir[1];
    if (dot < 0) {
      const tx = dir[0] - n[0] * dot;
      const ty = dir[1] - n[1] * dot;
      const tlen = Math.hypot(tx, ty);
      if (tlen < 1e-6) return [fromM(p1)];
      dir = [tx / tlen, ty / tlen];
    }
    const exit2 = rayCrossingsM(segs, p1, dir)[0] ?? Infinity;
    const leg2 = Math.min(
      budgetM - perpDepth,
      dC * 0.6,
      exit2 - Math.min(ENTRY_EDGE_MARGIN_M, exit2 * 0.25),
    );
    // 数 m の折れ端は矢印としてノイズなので付けない。
    if (leg2 < 3) return [fromM(p1)];
    return [fromM(p1), fromM([p1[0] + dir[0] * leg2, p1[1] + dir[1] * leg2])];
  };

  // 進入方向 (prev→entry) に対する折り返しの許容判定。折り 150° 超の方向は、
  // 他に成立する方向がある限り避ける (実測: 札幌 旭小 — 門をアプローチが通り
  // 過ぎる形になり、辺の法線が進入方向の 157° 折り返しになった)。
  const COS_MAX_FOLD = Math.cos((ENTRY_MAX_FOLD_DEG * Math.PI) / 180);
  let approach: [number, number] | null = null;
  if (prev) {
    const pm = toM(prev);
    const pl = Math.hypot(pm[0], pm[1]);
    if (pl > 1) approach = [-pm[0] / pl, -pm[1] / pl];
  }
  const foldOk = (dir: [number, number]): boolean =>
    !approach || approach[0] * dir[0] + approach[1] * dir[1] >= COS_MAX_FOLD;

  // ± 両符号を試す。両方成立するとき (進入点が内側の場合など) は奥行きの深い方。
  const attemptBoth = (
    n: [number, number],
    ok: (dir: [number, number]) => boolean,
  ): Array<[number, number]> | null => {
    const nn: [number, number] = [-n[0], -n[1]];
    const s1 = ok(n) ? spanFor(n) : null;
    const s2 = ok(nn) ? spanFor(nn) : null;
    if (s1 && s2) {
      const deeper = s1.tOut - s1.tIn >= s2.tOut - s2.tIn;
      return deeper
        ? (buildFor(n, s1) ?? buildFor(nn, s2))
        : (buildFor(nn, s2) ?? buildFor(n, s1));
    }
    if (s1) return buildFor(n, s1);
    if (s2) return buildFor(nn, s2);
    return null;
  };

  const runRound = (
    ok: (dir: [number, number]) => boolean,
  ): Array<[number, number]> | null => {
    // 1) 近傍辺の法線 = 直角進入 (近い順)。
    for (const { edge } of cands.slice(0, 3)) {
      const elen = Math.hypot(edge[0], edge[1]);
      const pts = attemptBoth([-edge[1] / elen, edge[0] / elen], ok);
      if (pts) return pts;
    }
    // 2) 進入点が鋭角の頂点のほぼ真上にある入口: どちらの辺の法線レイも先細りの
    //    くさびを掠めて不成立になる (実測: 新川ツインビル地区の南西角 — 中心延長へ
    //    落ち、境界の外側を 100m 並走する矢印になった)。角の二等分線はくさびの
    //    内部を通る唯一の安定方向なので、それでダイブする。
    let vi = -1;
    let vd = Infinity;
    for (let i = 0; i < ring.length; i++) {
      const m = toM(ring[i]!);
      const d = Math.hypot(m[0], m[1]);
      if (d < vd) {
        vd = d;
        vi = i;
      }
    }
    if (vi >= 0 && vd <= ENTRY_BOUNDARY_MAX_DIST_M) {
      const N = ring.length;
      const isClosed =
        ring[0]![0] === ring[N - 1]![0] && ring[0]![1] === ring[N - 1]![1];
      const M = isClosed ? N - 1 : N;
      const v = toM(ring[vi % M]!);
      const pv = toM(ring[(vi - 1 + M) % M]!);
      const nv = toM(ring[(vi + 1) % M]!);
      const u1: [number, number] = [pv[0] - v[0], pv[1] - v[1]];
      const u2: [number, number] = [nv[0] - v[0], nv[1] - v[1]];
      const l1 = Math.hypot(u1[0], u1[1]);
      const l2len = Math.hypot(u2[0], u2[1]);
      if (l1 > 0 && l2len > 0) {
        const bx = u1[0] / l1 + u2[0] / l2len;
        const by = u1[1] / l1 + u2[1] / l2len;
        const bl = Math.hypot(bx, by);
        if (bl > 1e-6) {
          const pts = attemptBoth([bx / bl, by / bl], ok);
          if (pts) return pts;
        }
      }
    }
    return null;
  };
  // 折り返しにならない方向を優先し、無ければ折り返し許容で再試行 (進入は必ず試みる)。
  return runRound(foldOk) ?? runRound(() => true);
}

/**
 * origin から dir (単位ベクトル) へ伸ばしたレイが境界セグメントと交差する距離 (m)
 * の昇順リスト。立っている辺自身との交差 (t≈0) は無視する。
 */
function rayCrossingsM(
  segs: Array<[[number, number], [number, number]]>,
  origin: [number, number],
  dir: [number, number],
): number[] {
  const ts: number[] = [];
  for (const [a, b] of segs) {
    const ex = b[0] - a[0];
    const ey = b[1] - a[1];
    const denom = dir[0] * ey - dir[1] * ex;
    if (Math.abs(denom) < 1e-9) continue; // 平行
    const wx = a[0] - origin[0];
    const wy = a[1] - origin[1];
    const t = (wx * ey - wy * ex) / denom; // レイ側のパラメータ (m)
    const s = (wx * dir[1] - wy * dir[0]) / denom; // セグメント側 [0,1]
    if (t > 0.5 && s >= 0 && s <= 1) ts.push(t);
  }
  return ts.sort((x, y) => x - y);
}

/**
 * ポリゴンの「中心」= 面積重心。凹形状で面積重心が外に出る場合は内部の代表点。
 * どちらも取れなければ頂点の単純平均 (最終フォールバック)。
 */
export function polygonInteriorCenter(
  ring: Array<[number, number]>,
): LngLat | null {
  const poly = closedPolygon(ring);
  if (!poly) return polygonCentroid(ring);
  try {
    const com = turfCenterOfMass(poly).geometry.coordinates as [number, number];
    if (booleanPointInPolygon(turfPoint(com), poly)) {
      return { lng: com[0], lat: com[1] };
    }
    // 凹形状 (C字の公園等) では面積重心が穴の中に落ちうる → 内部の代表点へ
    const on = turfPointOnFeature(poly).geometry.coordinates as [number, number];
    return { lng: on[0], lat: on[1] };
  } catch {
    return polygonCentroid(ring);
  }
}

/** ring から turf polygon を作る (閉じていなければ閉じる)。3点未満は null。 */
function closedPolygon(ring: Array<[number, number]>) {
  if (ring.length < 3) return null;
  const r = ring.slice();
  const f = r[0]!;
  const l = r[r.length - 1]!;
  if (f[0] !== l[0] || f[1] !== l[1]) r.push([f[0], f[1]]);
  if (r.length < 4) return null;
  try {
    return turfPolygon([r]);
  } catch {
    return null;
  }
}

/**
 * ポリゴン内部の候補点を撒く (吹き出し線の着地点の候補)。
 *
 * 中心 (面積重心) と、そこから各頂点へ向かう途中の点を返す。fractions は
 * 中心→頂点 の比 (凹形状で外に出た点は捨てる)。
 *
 * 縁寄り (0.85) まで含めるのが重要: 矢印は中心を指すので、中心付近の候補だけだと
 * どれを選んでも吹き出し線が矢印を跨ぐ。縁寄りの候補があると、矢印の反対側から
 * 短い線で着地できる。
 */
export function polygonInteriorPoints(
  ring: Array<[number, number]>,
  fractions: number[] = [0.3, 0.55, 0.85],
): LngLat[] {
  const center = polygonInteriorCenter(ring);
  if (!center) return [];
  const poly = closedPolygon(ring);
  const out: LngLat[] = [center];
  for (const [lng, lat] of ring) {
    for (const f of fractions) {
      const p: LngLat = {
        lng: center.lng + (lng - center.lng) * f,
        lat: center.lat + (lat - center.lat) * f,
      };
      // 凹形状では中心→頂点の途中が外に出ることがある
      if (!poly || booleanPointInPolygon(turfPoint([p.lng, p.lat]), poly)) {
        out.push(p);
      }
    }
  }
  return out;
}

/**
 * ポリゴン境界線上で、指定点(property)に最も近い点を返す。
 * turf.nearestPointOnLine を使用 (大圏距離での最近接)。
 */
export function nearestPointOnPolygonBoundary(
  ring: Array<[number, number]>,
  from: LngLat,
): LngLat | null {
  if (ring.length < 2) return null;
  // ringは閉じてなくてもlineStringにできる
  const boundary = lineString(ring);
  const ref = turfPoint([from.lng, from.lat]);
  const nearest = nearestPointOnLine(boundary, ref);
  const c = nearest.geometry.coordinates;
  if (c.length < 2) return null;
  return { lng: c[0]!, lat: c[1]! };
}

export interface RouteEndpointResult {
  /** 経路の終点座標 */
  coords: LngLat;
  /** どの方法で決定したか (UIで出典表示用) */
  source: 'entrance' | 'boundary' | 'centroid';
  /** OSM入口を選んだ場合の入口情報 */
  entrance?: EntranceNode;
}

/**
 * 入口を採用するかの許容差 (m)。物件から見て最寄り入口が「最寄り境界点 + この値」
 * 以内なら実在の入口(門)を終点に採用する。大きく超える場合は、物件側に門が
 * マッピングされていない = 反対側の門しかない状況なので、遠回りを避けて境界端で止める。
 */
const ENTRANCE_TOLERANCE_M = 60;

/**
 * 物件 → 避難場所への経路終点を「入り口優先」で決定。
 *
 * 1. polygon(±buffer)内のOSM `entrance=*` / `barrier=gate` ノードを取得
 *    → 物件から最も近い入口が「最寄り境界点とほぼ同等距離」なら採用 ("source: entrance")
 * 2. 入口が無い/遠すぎる場合は polygon境界線上の最近接点 ("source: boundary")
 * 3. polygon自体無ければ polygon重心 ("source: centroid")
 *
 * OSMリクエスト失敗時は境界フォールバックに進む。
 */
export interface ResolveRouteEndpointOptions {
  /**
   * OSM の入口(門)を探すか。既定 true。
   *
   * ポリゴンが実形状ではなく暫定円 (EvacuationPlace.approxShape) の場合は false に
   * する。円は実際の敷地境界ではないため、その円内の「入口」を採っても意味が薄く、
   * Overpass の1往復が丸ごと無駄になる。
   */
  entrances?: boolean;
}

export async function resolveRouteEndpoint(
  property: LngLat,
  polygonRing: Array<[number, number]> | null | undefined,
  opts: ResolveRouteEndpointOptions = {},
): Promise<RouteEndpointResult | null> {
  if (!polygonRing || polygonRing.length < 3) {
    return null;
  }

  // 先に境界最近接点を求めておく (入口採否の基準 + フォールバック)
  const boundaryPoint = nearestPointOnPolygonBoundary(polygonRing, property);
  const boundaryDist = boundaryPoint
    ? haversineMeters(property, boundaryPoint)
    : Infinity;

  // 1. OSM入口を取得し、近い入口のみ採用
  if (opts.entrances !== false) {
    try {
      const entrances = await fetchEntrancesInPolygon(polygonRing);
      if (entrances.length > 0) {
        // 物件から最も近い入口を選ぶ
        let best: EntranceNode | null = null;
        let bestDist = Infinity;
        for (const e of entrances) {
          const d = haversineMeters(property, { lng: e.lng, lat: e.lat });
          if (d < bestDist) {
            best = e;
            bestDist = d;
          }
        }
        // 最寄り境界とほぼ同等以内の入口だけ採用 (遠い門への迂回を防ぐ)
        if (best && bestDist <= boundaryDist + ENTRANCE_TOLERANCE_M) {
          return {
            coords: { lng: best.lng, lat: best.lat },
            source: 'entrance',
            entrance: best,
          };
        }
      }
    } catch {
      // OSM障害時はboundary fallbackへ
    }
  }

  // 2. 境界最近接点
  if (boundaryPoint) {
    return { coords: boundaryPoint, source: 'boundary' };
  }

  // 3. 重心 (最終フォールバック)
  const centroid = polygonCentroid(polygonRing);
  if (centroid) {
    return { coords: centroid, source: 'centroid' };
  }

  return null;
}

/**
 * 物件 footprint の「出口」を決めて経路の始点にする。
 * footprint 内の OSM 入口ノードのうち、**目的地(towardCoords)に最も近い入口**
 * (=その方向の出口) を返す。Directions が入口→前面道路へスナップするので、
 * 経路が「入口前の道路」から始まる。入口が無い/取得失敗なら null (呼び出し側で
 * 物件座標にフォールバック)。resolveRouteEndpoint の対称版。
 */
export async function resolveRouteStart(
  footprintRing: Array<[number, number]> | null | undefined,
  towardCoords: LngLat,
): Promise<LngLat | null> {
  if (!footprintRing || footprintRing.length < 3) return null;
  try {
    const entrances = await fetchEntrancesInPolygon(footprintRing);
    let best: EntranceNode | null = null;
    let bestDist = Infinity;
    for (const e of entrances) {
      const d = haversineMeters(towardCoords, { lng: e.lng, lat: e.lat });
      if (d < bestDist) {
        best = e;
        bestDist = d;
      }
    }
    if (best) return { lng: best.lng, lat: best.lat };
  } catch {
    // OSM障害時は null → 物件座標フォールバック
  }
  return null;
}

/**
 * start→end の直線に対して**垂直に左右へオフセットした経由地**を生成する。
 *
 * Mapbox の徒歩プロファイルは代替経路をほぼ1本しか返さないため、これらの経由地を
 * 通す経路 (start→via→end) を作ることで「別の道を通る候補」を人工的に得る。
 * オフセット量は直線距離に比例させ、極端に短い/長い経路でも自然な迂回幅になるよう
 * [minMeters, maxMeters] でクランプする。各オフセットにつき左右2点を返す。
 */
export function perpendicularViaPoints(
  start: LngLat,
  end: LngLat,
  fractions: number[] = [0.35, 0.7],
  minMeters = 80,
  maxMeters = 500,
): LngLat[] {
  const s = turfPoint([start.lng, start.lat]);
  const e = turfPoint([end.lng, end.lat]);
  const straight = haversineMeters(start, end);
  if (straight < 1) return [];
  const mid = turfMidpoint(s, e);
  const brng = turfBearing(s, e); // -180..180 (真北0)
  const out: LngLat[] = [];
  for (const f of fractions) {
    const offM = Math.min(maxMeters, Math.max(minMeters, straight * f));
    for (const side of [90, -90]) {
      const d = turfDestination(mid, offM / 1000, brng + side, {
        units: 'kilometers',
      });
      const c = d.geometry.coordinates;
      out.push({ lng: c[0]!, lat: c[1]! });
    }
  }
  return out;
}

/**
 * 経由点群を start→end の直線に射影し、進行方向(t 昇順)へ整列した新配列を返す。
 * 経路を「必ず通過する経由点」に通す際、始点→終点へ単調進行させるための順序付け。
 * 短距離なので等距圧平面 (start.lat 基準) で十分。入力は非破壊。
 */
export function orderByProgress(
  vias: LngLat[],
  start: LngLat,
  end: LngLat,
): LngLat[] {
  if (vias.length <= 1) return [...vias];
  const kx = Math.cos((start.lat * Math.PI) / 180) * 111_320;
  const ky = 110_540;
  const sx = start.lng * kx;
  const sy = start.lat * ky;
  const dx = end.lng * kx - sx;
  const dy = end.lat * ky - sy;
  const len2 = dx * dx + dy * dy;
  const t = (v: LngLat) => {
    if (len2 === 0) return 0;
    const vx = v.lng * kx - sx;
    const vy = v.lat * ky - sy;
    return (vx * dx + vy * dy) / len2;
  };
  return [...vias].sort((a, b) => t(a) - t(b));
}

/**
 * 経路 (LineString) が polygon を貫通した場合、最初の境界交点で切り詰める。
 *
 * Mapbox Directions API は終点を最寄り道路にスナップするため、目的地が
 * polygon境界上でも経路が polygon内部 (公園内の通路等) まで延びることがある。
 * 視覚上「避難場所の入り口で経路を止める」要件を満たすため、polygon境界で切る。
 *
 * 動作:
 *   1. 物件側から順に route の vertex を確認
 *   2. 初めて polygon内部に入った vertex を見つける
 *   3. その直前の vertex (外側) との segment と polygon境界の交点を計算
 *   4. [start..外側vertex, 交点] を返す
 *
 * polygonに一度も入らない経路 (= polygon外で終わる) はそのまま返す。
 * 経路の始点が polygon内 (= 物件が polygon内側) は想定外なのでそのまま返す。
 */
export function trimRouteAtPolygonBoundary(
  route: Array<[number, number]>,
  ring: Array<[number, number]>,
): Array<[number, number]> {
  if (route.length < 2 || ring.length < 3) return route;

  // turfPolygon は閉じた ring を要求
  const isClosed =
    ring[0]?.[0] === ring[ring.length - 1]?.[0] &&
    ring[0]?.[1] === ring[ring.length - 1]?.[1];
  const closedRing = isClosed ? ring : [...ring, ring[0]!];
  let poly;
  try {
    poly = turfPolygon([closedRing]);
  } catch {
    return route; // invalid polygon
  }

  // 始点が polygon内なら trim する意味がない (= 物件が polygon内)
  if (booleanPointInPolygon(turfPoint(route[0]!), poly)) {
    return route;
  }

  // 最初に polygon内に入った vertex を探す
  let firstInsideIdx = -1;
  for (let i = 1; i < route.length; i++) {
    if (booleanPointInPolygon(turfPoint(route[i]!), poly)) {
      firstInsideIdx = i;
      break;
    }
  }
  if (firstInsideIdx < 0) return route; // 一度も入らない → trim 不要

  // 外側 vertex (firstInsideIdx-1) と 内側 vertex (firstInsideIdx) の segment と境界の交点
  const outsidePt = route[firstInsideIdx - 1]!;
  const insidePt = route[firstInsideIdx]!;
  const segment = lineString([outsidePt, insidePt]);
  const intersections = lineIntersect(segment, poly);
  if (intersections.features.length === 0) {
    // 想定外: 外→内 segmentなのに交点なし。安全側で外側 vertex まで返す。
    return route.slice(0, firstInsideIdx);
  }
  // turf の lineIntersect は line1 (= 外側点→内側点) 上の位置順で交点を返すため、
  // features[0] が最初に境界を跨ぐ点になる (リングの並び順には依存しないことを実測で確認)。
  const cross = intersections.features[0]!.geometry.coordinates as [
    number,
    number,
  ];
  return [...route.slice(0, firstInsideIdx), cross];
}

/**
 * 経路の始点が polygon 内 (= 物件 footprint の内側から始まる) 場合、最初に polygon を
 * 出た境界交点を始点にする。物件座標を始点にした経路が塗り (fill) の内側を通る分を落とす。
 * trimRouteAtPolygonBoundary の対称版 (あちらは終点=避難先への貫通を落とす)。
 *
 * 始点が polygon 外 (= 物件の縁/入口から始まる) 経路はそのまま返す。
 */
export function trimRouteStartInsidePolygon(
  route: Array<[number, number]>,
  ring: Array<[number, number]>,
): Array<[number, number]> {
  if (route.length < 2 || ring.length < 3) return route;

  const isClosed =
    ring[0]?.[0] === ring[ring.length - 1]?.[0] &&
    ring[0]?.[1] === ring[ring.length - 1]?.[1];
  const closedRing = isClosed ? ring : [...ring, ring[0]!];
  let poly;
  try {
    poly = turfPolygon([closedRing]);
  } catch {
    return route; // invalid polygon
  }

  // 始点が polygon外なら trim 不要 (既に物件の外/縁から始まっている)
  if (!booleanPointInPolygon(turfPoint(route[0]!), poly)) return route;

  // 最初に polygon外へ出た vertex を探す
  let firstOutsideIdx = -1;
  for (let i = 1; i < route.length; i++) {
    if (!booleanPointInPolygon(turfPoint(route[i]!), poly)) {
      firstOutsideIdx = i;
      break;
    }
  }
  if (firstOutsideIdx < 0) return route; // 全頂点が内側 (異常) → そのまま

  // 内側 vertex (firstOutsideIdx-1) と 外側 vertex (firstOutsideIdx) の segment と境界の交点
  const insidePt = route[firstOutsideIdx - 1]!;
  const outsidePt = route[firstOutsideIdx]!;
  const segment = lineString([insidePt, outsidePt]);
  const intersections = lineIntersect(segment, poly);
  if (intersections.features.length === 0) {
    // 想定外: 内→外 segmentなのに交点なし。安全側で外側 vertex 以降を返す。
    return route.slice(firstOutsideIdx);
  }
  const cross = intersections.features[0]!.geometry.coordinates as [
    number,
    number,
  ];
  return [cross, ...route.slice(firstOutsideIdx)];
}

/**
 * 「浅い折れ」(頂点を除去しても経路長がほとんど変わらないジグザグ) を除去する。
 *
 * Directions のスナップ点が左右交互に並ぶと、実質一本道の経路が W 字に見える
 * (実測: 阪本小学校への 134m 経路に 47°/44°/47° の折れ、迂回量は各 ~1-4m)。
 * douglas-peucker (simplifyRouteCoords) は「直線からの距離」基準のため振れ幅が
 * 許容差未満のジグザグを残す。ここでは「迂回量 = ab+bc-ac」基準で、本物の街角
 * (直角 = 迂回量数十m) は残しつつ、意味のない折れだけを畳む。
 */
/** ジグザグ判定: 「曲がっている」とみなす最小折れ角 (度)。 */
const ZIGZAG_MIN_TURN_DEG = 10;
/**
 * 除去する頂点が弦 (前後の頂点を結ぶ線分) から離れていてよい最大距離 (m)。
 * 迂回量だけを条件にすると、脚の長い折れ (数百 m の脚を持つ Z 字 = 並行街路への
 * 乗り換え等) は迂回量数 m でも弦が横へ ~√(脚長×迂回量) ≈ 30-50m 逸脱し、
 * 街区や川を斜めに横切る矢印になる (実測: 新川ツインビル地区 681m 候補で 38m 逸脱)。
 * simplify (douglas-peucker) の許容 12m と同じスケールに揃える。
 */
const SHALLOW_BEND_MAX_DEVIATION_M = 12;

export function removeShallowBends(
  route: Array<[number, number]>,
  detourEpsM = 8,
  maxDeviationM = SHALLOW_BEND_MAX_DEVIATION_M,
): Array<[number, number]> {
  if (route.length <= 2) return route;
  const m = (p: [number, number], q: [number, number]) =>
    haversineMeters({ lng: p[0], lat: p[1] }, { lng: q[0], lat: q[1] });
  // 除去対象は**元経路上でジグザグ (折れ符号の交代) の一員**である頂点だけに限る。
  // 迂回量だけを条件にすると、同方向へ曲がり続ける本物のカーブ (1回の除去は常に
  // 迂回量小) が greedy の反復で弦まで潰れ、実道路から 10m 超逸脱する
  // (レビュー実測: r=60m の 1/4 円弧が 17.6m 逸脱)。スナップ由来のノイズは左右への
  // 振れ = 符号交代なので、交代の一員に限れば W 字は従来どおり畳める。
  const sign = (i: number, pts: Array<[number, number]>): number => {
    const kx = Math.cos(pts[i]![1] * (Math.PI / 180)) * 111_320;
    const ky = 110_540;
    const v1x = (pts[i]![0] - pts[i - 1]![0]) * kx;
    const v1y = (pts[i]![1] - pts[i - 1]![1]) * ky;
    const v2x = (pts[i + 1]![0] - pts[i]![0]) * kx;
    const v2y = (pts[i + 1]![1] - pts[i]![1]) * ky;
    const cross = v1x * v2y - v1y * v2x;
    const deg =
      (Math.atan2(Math.abs(cross), v1x * v2x + v1y * v2y) * 180) / Math.PI;
    if (deg < ZIGZAG_MIN_TURN_DEG) return 0;
    return cross > 0 ? 1 : -1;
  };
  const signs = route.map((_, i) =>
    i > 0 && i < route.length - 1 ? sign(i, route) : 0,
  );
  const removable = route.map((_, i) => {
    if (i === 0 || i === route.length - 1) return false;
    const s = signs[i]!;
    if (s === 0) return false;
    return signs[i - 1] === -s || signs[i + 1] === -s;
  });

  const pts = [...route];
  const flags = [...removable];
  let changed = true;
  while (changed && pts.length > 2) {
    changed = false;
    // 迂回量が最小の除去対象頂点を探し、閾値未満なら除去 (greedy に1点ずつ)
    let bestIdx = -1;
    let bestDetour = Infinity;
    for (let i = 1; i < pts.length - 1; i++) {
      if (!flags[i]) continue;
      // 弦からの垂直逸脱が大きい頂点は、迂回量が小さくても除去しない (弦への置換で
      // 経路が横へ大きくずれ、道でない場所を横切るため)。
      if (perpDeviationMeters(pts[i - 1]!, pts[i]!, pts[i + 1]!) > maxDeviationM) {
        continue;
      }
      const detour =
        m(pts[i - 1]!, pts[i]!) +
        m(pts[i]!, pts[i + 1]!) -
        m(pts[i - 1]!, pts[i + 1]!);
      if (detour < bestDetour) {
        bestDetour = detour;
        bestIdx = i;
      }
    }
    if (bestIdx >= 0 && bestDetour < detourEpsM) {
      pts.splice(bestIdx, 1);
      flags.splice(bestIdx, 1);
      changed = true;
    }
  }
  return pts;
}

/** マイクロスパー: 「短い脚」とみなす最大長 (m)。除去による逸脱はこの長さ以下。 */
const MICRO_SPUR_MAX_LEG_M = 12;
/**
 * マイクロスパー: 除去対象とみなす最小折れ角 (度)。横断歩道ジグザグの実測は 86-87°
 * (斜め横断) — 90 にすると素通りする。実在の街角は脚が街路 (数十m) なので
 * MICRO_SPUR_MAX_LEG_M のゲートが守る。
 */
const MICRO_SPUR_MIN_TURN_DEG = 60;
/**
 * マイクロスパー: 除去する頂点の弦からの最大逸脱 (m)。脚長ゲートだけだと、
 * 角補完が挿入した**実在の交差点の角** (隣に横断歩道由来の頂点が ~10m 至近にあり
 * 脚が短い) まで削除し、経路が交差点を斜めに省略する (実測: 阪本小 A1/A4/A10)。
 * ジグザグの逸脱は ~3-6m、実在の角の逸脱は ~8m 超 — 逸脱で区別する。
 */
const MICRO_SPUR_MAX_DEVIATION_M = 6.5;
/** これを超える折れは「折り返し」— 実在の角ではあり得ないため逸脱ゲートで守らない。 */
const MICRO_SPUR_FOLD_TURN_DEG = 150;
/**
 * 中間頂点 (アンカー隣接を除く) の「行き過ぎ楔」閾値。実在の交差点の角は高々 ~95°、
 * 門の直角進入の折れも ~90+α (アンカー隣接) なので、中間で 100° を超える折れは
 * 交差点を行き過ぎて戻る楔 (実測: 横浜ランドマークタワー — 11m 突き出し 108°)。
 * 短脚 (≤12m) のときだけ適用し、大きな実迂回は触らない。
 */
const MICRO_SPUR_OVERSHOOT_TURN_DEG = 100;
/**
 * 行き過ぎ楔の脚長ゲート (m)。通常のスパー (≤12m) より広い 20m — 楔の脚は
 * 交差点の張り出し分だけ長くなる (実測: 横浜 A3 — 脚 25/13m の 108° 楔が
 * 12m ゲートを 1m 超えてすり抜けた)。>100° 自体が非文法なので広めでも誤爆しない。
 */
const MICRO_SPUR_OVERSHOOT_MAX_LEG_M = 20;
/**
 * 交差点の織り目 (横断歩道・歩道由来の低振幅ジグザグ) の除去条件。
 * 両脚 ≤20m かつ弦からの逸脱 ≤5m の中間頂点は、折れ角に関わらず除去する
 * (実測: 横浜 A1 — 栄本町線の交差点で 18/9/6m の織り目が車道から 4-5m 揺れた。
 * 簡略化許容を 2m に絞ってカーブ追従を得た代償で露出したノイズ)。
 * 90° の実在角は短脚でも逸脱 >5m になるため対象外 (脚 8/8m の直角で逸脱 5.7m)。
 */
const WEAVE_MAX_LEG_M = 20;
const WEAVE_MAX_DEVIATION_M = 5;

/**
 * 短い脚を持つ鋭角スパー (V 字ジャグ) を除去する。
 *
 * 横断歩道の島・中心線スナップのガードが残した生ジオメトリの断片は、数 m の脚で
 * 90° 超に折れる形になる。案内図スケールでは実在の歩行転回ではあり得ない形
 * (実在の街角は両脚が街路 = 数十 m)。平滑化の許容を絞る (12m→5m) とこの断片が
 * 露出するため、専用に除去する。除去による経路の逸脱は脚長 (≤12m) で有界。
 */
export function removeMicroSpurs(
  route: Array<[number, number]>,
): Array<[number, number]> {
  if (route.length <= 2) return route;
  const m = (p: [number, number], q: [number, number]) =>
    haversineMeters({ lng: p[0], lat: p[1] }, { lng: q[0], lat: q[1] });
  const pts = [...route];
  const turnDeg = (i: number): number => {
    const kx = Math.cos(pts[i]![1] * (Math.PI / 180)) * 111_320;
    const ky = 110_540;
    const v1x = (pts[i]![0] - pts[i - 1]![0]) * kx;
    const v1y = (pts[i]![1] - pts[i - 1]![1]) * ky;
    const v2x = (pts[i + 1]![0] - pts[i]![0]) * kx;
    const v2y = (pts[i + 1]![1] - pts[i]![1]) * ky;
    return (
      (Math.atan2(Math.abs(v1x * v2y - v1y * v2x), v1x * v2x + v1y * v2y) *
        180) /
      Math.PI
    );
  };
  // パス1: 折り返し (>150°) は脚の長さ・逸脱に関わらず除去する。矢印の文法に
  // 150° 超の折れは存在しない (最大90°)。脚長ゲート (≤12m) は「側道への 23〜44m
  // の往復」を素通しにする (実測: 札幌 旭小の候補 — 経由地強制による往復)。
  // アンカーの隣も対象 (直角化の足は 90° なので誤爆しない)。
  // 突き出しの根元と先端は**両方**が折り候補になる — 隣接脚の最大値が最小の頂点
  // (= 両脚とも短い先端) から除去する。根元側を先に消すと先端が残り、本物の角が
  // 消える (実測: 155° 先端 + 168° 根元の対で根元が先に消えた)。
  let changed = true;
  while (changed && pts.length > 2) {
    changed = false;
    let best = -1;
    let bestMaxLeg = Infinity;
    for (let i = 1; i < pts.length - 1; i++) {
      const legA = m(pts[i - 1]!, pts[i]!);
      const legB = m(pts[i]!, pts[i + 1]!);
      // 短脚なら「行き過ぎ楔」閾値 (100°) を適用。
      //
      // 以前はアンカー隣接 (i<2 / i>len-3) を除外し 150° 折れしか消さなかった。
      // 直角化が挿す垂線の足 (~90°) を守るためだが、100° の閾値が既にそれを守る。
      // 除外していると、避難先へ入る直前の横断歩道の迂回が残る
      // (実測: プラウドフラット門前仲町Ⅰ — 木場公園一帯の手前で 14m/17m の脚で
      //  107° 折れ、公園の入口直前で線がカクッと曲がって見えた)。
      const limit =
        Math.min(legA, legB) <= MICRO_SPUR_OVERSHOOT_MAX_LEG_M
          ? MICRO_SPUR_OVERSHOOT_TURN_DEG
          : MICRO_SPUR_FOLD_TURN_DEG;
      if (turnDeg(i) <= limit) continue;
      const maxLeg = Math.max(legA, legB);
      if (maxLeg < bestMaxLeg) {
        bestMaxLeg = maxLeg;
        best = i;
      }
    }
    if (best >= 0) {
      pts.splice(best, 1);
      if (best < pts.length && m(pts[best - 1]!, pts[best]!) < 0.5) {
        pts.splice(best, 1);
      }
      changed = true;
    }
  }
  // パス1.5: 交差点の織り目除去 (両脚≤20m・逸脱≤5m)。折れ角は問わない —
  // 織り目は 20-50° の浅い折れで、鋭角ゲート (60°) に掛からない。
  changed = true;
  while (changed && pts.length > 2) {
    changed = false;
    for (let i = 1; i < pts.length - 1; i++) {
      const legA = m(pts[i - 1]!, pts[i]!);
      const legB = m(pts[i]!, pts[i + 1]!);
      if (Math.max(legA, legB) > WEAVE_MAX_LEG_M) continue;
      if (
        perpDeviationMeters(pts[i - 1]!, pts[i]!, pts[i + 1]!) >
        WEAVE_MAX_DEVIATION_M
      ) {
        continue;
      }
      pts.splice(i, 1);
      changed = true;
      break;
    }
  }

  // パス2: 短い脚の鋭角スパー除去。
  changed = true;
  while (changed && pts.length > 2) {
    changed = false;
    // 端 (始点/終点アンカー) の隣は対象外: アンカーの直角化 (route-snap) が挿入する
    // 「垂線の足」は短い脚 + 90° 折れの形で、ここで削るとリードインの斜め線が再発する。
    for (let i = 2; i < pts.length - 2; i++) {
      const legA = m(pts[i - 1]!, pts[i]!);
      const legB = m(pts[i]!, pts[i + 1]!);
      if (Math.min(legA, legB) > MICRO_SPUR_MAX_LEG_M) continue;
      const kx = Math.cos(pts[i]![1] * (Math.PI / 180)) * 111_320;
      const ky = 110_540;
      const v1x = (pts[i]![0] - pts[i - 1]![0]) * kx;
      const v1y = (pts[i]![1] - pts[i - 1]![1]) * ky;
      const v2x = (pts[i + 1]![0] - pts[i]![0]) * kx;
      const v2y = (pts[i + 1]![1] - pts[i]![1]) * ky;
      const deg =
        (Math.atan2(Math.abs(v1x * v2y - v1y * v2x), v1x * v2x + v1y * v2y) *
          180) /
        Math.PI;
      if (deg < MICRO_SPUR_MIN_TURN_DEG) continue;
      // 弦から大きく逸脱する頂点 (実在の角) は脚が短くても除去しない。ただし
      // **折り返し (>150°) は除外** — 矢印の文法に 150° 超の折れは存在しない
      // (最大90°) ため常に突き出しであり、特に「同一点へ戻る完全往復」は弦が
      // 退化して逸脱 = 脚長となり、このゲートが往復を保護してしまう
      // (実測: 旭小 A1 — 側道への 11m 往復と 7m 横の 155° 突き出し)。
      if (
        deg <= MICRO_SPUR_FOLD_TURN_DEG &&
        perpDeviationMeters(pts[i - 1]!, pts[i]!, pts[i + 1]!) >
          MICRO_SPUR_MAX_DEVIATION_M
      ) {
        continue;
      }
      pts.splice(i, 1);
      // 往復の除去で隣接頂点が同一点 (弦の退化) になったら片方をまとめる。
      if (i < pts.length && m(pts[i - 1]!, pts[i]!) < 0.5) {
        pts.splice(i, 1);
      }
      changed = true;
      break;
    }
  }
  return pts;
}

/** 頂点 b の、弦 a–c からの垂直距離 (m, ローカルメートル空間)。 */
function perpDeviationMeters(
  a: [number, number],
  b: [number, number],
  c: [number, number],
): number {
  const kx = Math.cos(b[1] * (Math.PI / 180)) * 111_320;
  const ky = 110_540;
  const acx = (c[0] - a[0]) * kx;
  const acy = (c[1] - a[1]) * ky;
  const abx = (b[0] - a[0]) * kx;
  const aby = (b[1] - a[1]) * ky;
  const l = Math.hypot(acx, acy);
  if (l <= 0) return Math.hypot(abx, aby);
  return Math.abs(acx * aby - acy * abx) / l;
}

/**
 * 経路の折れ角の合計 (度)。「どちらの経路がまっすぐか」の比較に使う。
 * 距離が拮抗する候補は折れの少ない方を優先する (実測: 阪本小学校で、北面発の
 * 71°折れ経路と西面発の直進経路が数mしか違わず、距離のみの順位だと折れが勝った)。
 */
export function routeTotalTurnDeg(route: Array<[number, number]>): number {
  if (route.length < 3) return 0;
  let total = 0;
  for (let i = 1; i < route.length - 1; i++) {
    const kx = Math.cos(route[i]![1] * (Math.PI / 180)) * 111_320;
    const ky = 110_540;
    const v1 = [
      (route[i]![0] - route[i - 1]![0]) * kx,
      (route[i]![1] - route[i - 1]![1]) * ky,
    ];
    const v2 = [
      (route[i + 1]![0] - route[i]![0]) * kx,
      (route[i + 1]![1] - route[i]![1]) * ky,
    ];
    const cross = v1[0]! * v2[1]! - v1[1]! * v2[0]!;
    const dot = v1[0]! * v2[0]! + v1[1]! * v2[1]!;
    total += (Math.atan2(Math.abs(cross), dot) * 180) / Math.PI;
  }
  return total;
}

/** 点がリングの内部か (開いたリングは閉じて判定)。 */
export function pointInsideRing(
  p: LngLat,
  ring: Array<[number, number]>,
): boolean {
  if (ring.length < 3) return false;
  const first = ring[0]!;
  const last = ring[ring.length - 1]!;
  const closed =
    first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first];
  try {
    return booleanPointInPolygon(turfPoint([p.lng, p.lat]), turfPolygon([closed]));
  } catch {
    return false;
  }
}

/**
 * 経路 (折れ線) と点の最短距離 (m)。
 * 経路候補が特定の橋の横断点を通っているかの判定に使う (通る経路は数m、別の橋は100m超)。
 */
export function minDistanceToRouteMeters(
  route: Array<[number, number]>,
  p: LngLat,
): number {
  if (route.length === 0) return Infinity;
  if (route.length === 1) {
    return haversineMeters({ lng: route[0]![0], lat: route[0]![1] }, p);
  }
  const np = nearestPointOnLine(lineString(route), turfPoint([p.lng, p.lat]), {
    units: 'kilometers',
  });
  return (np.properties.dist ?? Infinity) * 1000;
}

/**
 * primary の境界最近点からリング周囲を ±offsetMeters 歩いた 2 点を返す。
 *
 * 経路候補の「端点バリエーション」用: OSM に出入り口が無い/ズレている物件・避難先で、
 * アルゴリズムが置いた端点 (目的地方向の境界最近点) と別の面にも端点を置いた候補を
 * 作る (Directions はその面の前面道路へスナップするので、別の道からのアプローチになる)。
 * offset は周長の 1/3 にクランプ — 小さい建物で反対側へ回り込み、2 点が同じ場所に
 * 落ちるのを防ぐ。リングが polygon にならない場合は空配列。
 */
export function boundaryPointsAround(
  ring: Array<[number, number]>,
  primary: LngLat,
  offsetMeters = 120,
): LngLat[] {
  if (ring.length < 3) return [];
  const first = ring[0]!;
  const last = ring[ring.length - 1]!;
  const closed =
    first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first];
  let line;
  let totalKm;
  try {
    line = lineString(closed);
    totalKm = turfLength(line, { units: 'kilometers' });
  } catch {
    return [];
  }
  if (!(totalKm > 0)) return [];
  const np = nearestPointOnLine(line, turfPoint([primary.lng, primary.lat]), {
    units: 'kilometers',
  });
  const baseKm = np.properties.location ?? 0;
  const offKm = Math.min(offsetMeters / 1000, totalKm / 3);
  const out: LngLat[] = [];
  for (const sign of [1, -1]) {
    let loc = (baseKm + sign * offKm) % totalKm;
    if (loc < 0) loc += totalKm;
    const p = turfAlong(line, loc, { units: 'kilometers' });
    const c = p.geometry.coordinates;
    out.push({ lng: c[0]!, lat: c[1]! });
  }
  return out;
}
